import React, { useMemo } from 'react'
import styled from 'styled-components/macro'
import {
  countGrowthEngineActiveSuppliers,
  getSupplierLadderProgress,
  getSupplierProgressCopy,
  shouldShowSupplierProgress,
} from 'src/utils/supplierLadderProgress'

/**
 * Light supplier progress toward 3 then 5.
 * Quiet editorial strip — benefit-led, not a Success Hub card.
 */
const SupplierProgress = ({
  activeSupplierCount,
  venueSuppliers,
  currentPlan,
  className,
  'data-testid': dataTestId = 'supplier-progress',
}) => {
  const count = useMemo(() => {
    if (typeof activeSupplierCount === 'number') {
      return activeSupplierCount
    }
    return countGrowthEngineActiveSuppliers(venueSuppliers)
  }, [activeSupplierCount, venueSuppliers])

  const progress = useMemo(() => getSupplierLadderProgress(count), [count])
  const copy = useMemo(() => getSupplierProgressCopy(progress), [progress])

  if (
    !shouldShowSupplierProgress({
      currentPlan,
      activeSupplierCount: count,
    }) ||
    !copy
  ) {
    return null
  }

  const fillPercent = Math.round(progress.progressRatio * 100)

  return (
    <Strip className={className} data-testid={dataTestId} role='status'>
      <Label data-testid='supplier-progress-label'>{copy.label}</Label>
      <Track
        aria-hidden='true'
        data-testid='supplier-progress-track'
        title={`${progress.activeSupplierCount} of ${progress.progressTarget}`}
      >
        <Fill style={{ width: `${fillPercent}%` }} />
      </Track>
      <Headline>{copy.headline}</Headline>
      <Detail>{copy.detail}</Detail>
    </Strip>
  )
}

/** Soft tint only — no border/shadow so it stays context, not another action card. */
const Strip = styled.div`
  background: ${({ theme }) => theme.colours?.sageWash || '#f6ffed'};
  border-radius: 8px;
  box-sizing: border-box;
  margin: 4px 0 20px;
  max-width: 640px;
  padding: 14px 16px;
  text-align: left;
  width: 100%;
`

const Label = styled.p`
  color: ${({ theme }) => theme.colours.seaweedGreen || '#389e0d'};
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.01em;
  line-height: 1.3;
  margin: 0 0 8px;
`

const Track = styled.div`
  background: ${({ theme }) => theme.colours.mayoWhite || '#fff'};
  border-radius: 2px;
  height: 3px;
  margin-bottom: 10px;
  overflow: hidden;
  width: 100%;
`

const Fill = styled.div`
  background: ${({ theme }) => theme.colours.lettuceGreen || '#7cb305'};
  height: 100%;
  max-width: 100%;
  transition: width 0.2s ease;
`

const Headline = styled.p`
  color: ${({ theme }) => theme.colours.bbqBlack || '#262626'};
  font-size: 14px;
  font-weight: 600;
  line-height: 1.35;
  margin: 0 0 2px;
`

const Detail = styled.p`
  color: ${({ theme }) => theme.colours.oliveGrey || '#595959'};
  font-size: 13px;
  line-height: 1.45;
  margin: 0;
  max-width: 52ch;
`

export default SupplierProgress
